import { Card, Badge } from '@/core/ui/components';
import type { ApiKey } from '@yawi/types';
import { Key, Clock, Shield } from 'lucide-react';

interface ApiKeysCardProps {
  apiKeys: ApiKey[];
}

const STATUS_BADGES: Record<string, { variant: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  active: { variant: 'success', label: 'Activa' },
  revoked: { variant: 'error', label: 'Revocada' },
  expired: { variant: 'warning', label: 'Expirada' },
};

function formatRelativeTime(dateStr: string | undefined): string {
  if (!dateStr) return 'Nunca';

  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'Hace un momento';
  if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return `Hace ${Math.floor(diff / 86400000)}d`;
  return date.toLocaleDateString();
}

export function ApiKeysCard({ apiKeys }: ApiKeysCardProps) {
  if (!apiKeys || apiKeys.length === 0) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
          <Key className="w-5 h-5" />
          API Keys
        </h3>
        <p className="text-theme-secondary text-center py-4">
          Sin API Keys registradas
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
        <Key className="w-5 h-5" />
        API Keys ({apiKeys.length})
      </h3>

      <div className="space-y-3">
        {apiKeys.map((key) => {
          const statusBadge = STATUS_BADGES[key.status] || STATUS_BADGES.active;

          return (
            <div
              key={key.id}
              className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="font-medium text-theme">{key.name}</span>
                </div>
                <Badge variant={statusBadge.variant} className="text-xs">
                  {statusBadge.label}
                </Badge>
              </div>

              <div className="flex items-center gap-4 text-xs text-theme-secondary">
                <span className="font-mono bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded">
                  {key.keyPreview}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Último uso: {formatRelativeTime(key.lastUsedAt)}
                </span>
              </div>

              {key.scopes && key.scopes.length > 0 && (
                <div className="flex items-center gap-1 mt-2">
                  <Shield className="w-3 h-3 text-theme-secondary" />
                  <div className="flex flex-wrap gap-1">
                    {key.scopes.slice(0, 3).map((scope) => (
                      <Badge key={scope} variant="default" className="text-xs">
                        {scope}
                      </Badge>
                    ))}
                    {key.scopes.length > 3 && (
                      <Badge variant="default" className="text-xs">
                        +{key.scopes.length - 3}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
